import { z } from 'zod';
import type { CustomerTierRuleToken } from '@sleed/bizcommerce-api';

/**
 * Customer tier-rule edit form. Mirrors `CustomerTierRuleToken` and the legacy admin grid
 * (Core/client/admin/src/pages/Customers/CustomerTierRules/index.js): tier + criteria type +
 * threshold value + time-frame are required, description ≤500, active flag. The criteria type
 * (`c_tier_ctype`) is a `CustomerTierCriteriaType` enum carried as its numeric value.
 */
export const customerTierRuleSchema = z.object({
	id: z.number().optional(),
	c_tier_id: z.number({ message: 'validation.required' }).int().min(1, 'validation.required'),
	c_tier_ctype: z.number({ message: 'validation.required' }).int(),
	c_tier_cvalue: z.number({ message: 'validation.required' }).positive('validation.positiveNumber'),
	time_frame_month: z.number({ message: 'validation.required' }).int('validation.wholeNumber').positive('validation.positiveNumber'),
	// CustomerTierRules.Description is 200 characters; the server refuses longer.
	descr: z.string().trim().max(200, 'validation.tooLong').nullable(),
	act: z.boolean()
});

export type CustomerTierRuleFormValues = z.infer<typeof customerTierRuleSchema>;

export const EMPTY_TIER_RULE: CustomerTierRuleFormValues = {
	c_tier_id: 0,
	c_tier_ctype: 0,
	c_tier_cvalue: 0,
	time_frame_month: 0,
	descr: '',
	act: true
};

export function tokenToForm(token: CustomerTierRuleToken): CustomerTierRuleFormValues {
	return {
		id: token.id,
		c_tier_id: token.c_tier_id ?? 0,
		c_tier_ctype: (token.c_tier_ctype as number) ?? 0,
		c_tier_cvalue: token.c_tier_cvalue ?? 0,
		time_frame_month: token.time_frame_month ?? 0,
		descr: token.descr ?? '',
		act: token.act ?? true
	};
}

export function formToToken(form: CustomerTierRuleFormValues, original: CustomerTierRuleToken | null): CustomerTierRuleToken {
	return {
		...(original ?? {}),
		id: form.id,
		c_tier_id: form.c_tier_id,
		c_tier_ctype: form.c_tier_ctype,
		c_tier_cvalue: form.c_tier_cvalue,
		time_frame_month: form.time_frame_month,
		descr: form.descr?.trim() || null,
		act: form.act
	};
}
